// src/pages/LoginPage.jsx
import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AtSymbolIcon } from "@heroicons/react/24/solid";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import carImage from "../../assets/Car_hero1.avif";
import logo from "../../assets/Full logo colour, white bg.jpg";
import logo2 from "../../assets/final_logo.png";

const ADMIN_ROLES = [
  "super-admin",
  "car-admin",
  "image-admin",
  "enquiry-admin",
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, loading, setAuth } = useAuth();

  const [step, setStep] = useState("email"); // "email" or "otp"
  const [emailForOtp, setEmailForOtp] = useState("");
  const [serverError, setServerError] = useState("");
  const [showNotAdminModal, setShowNotAdminModal] = useState(false);

  // Email form
  const {
    register: regEmail,
    handleSubmit: onEmailSubmit,
    formState: { errors: emailErr, isSubmitting: sending },
  } = useForm();

  // OTP form
  const {
    register: regOtp,
    handleSubmit: onOtpSubmit,
    formState: { errors: otpErr, isSubmitting: verifying },
  } = useForm();

  if (loading) return null;

  // If already logged in, redirect by role
  if (user) {
    const roles = user.roles || [];
    if (roles.includes("super-admin"))
      return <Navigate to="/admin/vehicles" replace />;
    if (roles.includes("car-admin"))
      return <Navigate to="/admin/vehicles" replace />;
    if (roles.includes("image-admin"))
      return <Navigate to="/admin/images" replace />;
    if (roles.includes("enquiry-admin"))
      return <Navigate to="/admin/requests" replace />;
    // non-admin → show modal
    return setShowNotAdminModal(true) || null;
  }

  // Step 1: send OTP
  const sendOtp = async ({ email }) => {
    setServerError("");
    try {
      await api.post("/auth/send-otp", { email });
      setEmailForOtp(email);
      setStep("otp");
    } catch (err) {
      setServerError(err.response?.data?.message || "Failed to send OTP");
    }
  };

  // Step 2: verify OTP
  const verifyOtp = async ({ otp }) => {
    setServerError("");
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email: emailForOtp,
        otp,
      });

      const roles = data.user.roles || [];
      const isAdmin = roles.some((r) => ADMIN_ROLES.includes(r));
      if (!isAdmin) {
        // show modal instead of toast/navigate
        setShowNotAdminModal(true);
        return;
      }

      // store tokens & user
      localStorage.setItem("accessToken", data.accessToken);
      setAuth({ user: data.user, token: data.accessToken });

      // redirect to the correct admin page
      if (roles.includes("super-admin")) navigate("/admin/vehicles");
      else if (roles.includes("car-admin")) navigate("/admin/vehicles");
      else if (roles.includes("image-admin")) navigate("/admin/images");
      else navigate("/admin/requests");
    } catch (err) {
      setServerError(err.response?.data?.message || "Invalid or expired OTP");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen font-sans">
      {/* Left: Branding */}
      <div className="hidden md:flex items-center justify-center bg-white pt-14">
        <div
          onClick={() => navigate("/")}
          className="text-center cursor-pointer"
        >
          <Link to="/">
            <img src={logo} alt="Logo" className="h-16 mx-auto" />
          </Link>
          <p className="text-primary text-lg opacity-75 mt-4">
            Admin panel login for ZORQK.
          </p>
          <img
            src={carImage}
            alt="Car"
            className="w-full h-full object-cover mt-6"
          />
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-8 bg-primary">
        <div className="w-full max-w-md px-2 space-y-6">
          <div
            onClick={() => navigate("/")}
            className="md:hidden text-center cursor-pointer"
          >
            <Link to="/">
              <img src={logo2} alt="Logo" className="h-20 mx-auto" />
            </Link>
          </div>

          <h2 className="text-2xl font-semibold text-white text-center">
            {step === "email"
              ? "Enter your admin email"
              : "Enter the code we sent"}
          </h2>

          {serverError && (
            <div className="text-sm text-red-600 bg-red-100 px-3 py-2 rounded">
              {serverError}
            </div>
          )}

          {step === "email" ? (
            <form onSubmit={onEmailSubmit(sendOtp)} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="email" className="block text-sm text-white">
                  Email
                </label>
                <div className="relative">
                  <AtSymbolIcon className="absolute left-3 top-1/2 h-5 w-5 text-secondary -translate-y-1/2" />
                  <input
                    id="email"
                    type="email"
                    {...regEmail("email", { required: true })}
                    placeholder="admin@example.com"
                    className="w-full pl-10 pr-4 py-2 rounded-lg text-primary"
                  />
                </div>
                {emailErr.email && (
                  <p className="text-sm text-red-500">
                    A valid email is required
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full py-3 bg-accent text-white rounded hover:bg-accent/90 transition"
              >
                {sending ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={onOtpSubmit(verifyOtp)} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="otp" className="block text-sm text-white">
                  6-digit code
                </label>
                <input
                  id="otp"
                  type="text"
                  maxLength={6}
                  {...regOtp("otp", { required: true, pattern: /^\d{6}$/ })}
                  placeholder="123456"
                  className="w-full px-4 py-2 rounded-lg text-primary text-center tracking-widest"
                />
                {otpErr.otp && (
                  <p className="text-sm text-red-500">Enter the 6-digit code</p>
                )}
              </div>
              <button
                type="submit"
                disabled={verifying}
                className="w-full py-3 bg-accent text-white rounded hover:bg-accent/90 transition"
              >
                {verifying ? "Verifying…" : "Verify code"}
              </button>
              <button
                type="button"
                onClick={() => setStep("email")}
                className="w-full py-2 text-secondary hover:underline"
              >
                Back to email
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Modal for non-admins */}
      {showNotAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-primary p-6 rounded-lg shadow-lg max-w-sm text-center">
            <h3 className="text-xl font-bold mb-4">Access Restricted</h3>
            <p className="mb-6">
              Your account has been created, but to access the admin panel you
              need to contact your Super Admin to assign you an admin role.
            </p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="px-4 py-2 bg-accent text-white rounded transition"
            >
              Go to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Below code is for password version
// // src/pages/LoginPage.jsx
// import { useState } from "react";
// import { useNavigate, Link, Navigate } from "react-router-dom";
// import { useForm } from "react-hook-form";
// import { yupResolver } from "@hookform/resolvers/yup";
// import {
//   EyeIcon,
//   EyeSlashIcon,
//   AtSymbolIcon,
//   LockClosedIcon,
// } from "@heroicons/react/24/solid";
// import api from "../../api/axios";
// import { useAuth } from "../../context/AuthContext";
// import { loginSchema } from "../../validation/auth.schema";
// import carImage from "../../assets/Car_hero1.avif"; // ← put your image here
// import logo from "../../assets/Full logo colour, white bg.jpg";
// import logo2 from "../../assets/Full logo colour.jpg";

// export default function LoginPage() {
//   const navigate = useNavigate();
//   const { user, loading, setAuth } = useAuth();
//   const [showPwd, setShowPwd] = useState(false);
//   const [serverError, setServerError] = useState("");

//   const {
//     register,
//     handleSubmit,
//     formState: { errors, isSubmitting },
//   } = useForm({
//     resolver: yupResolver(loginSchema),
//     defaultValues: { email: "", password: "" },
//   });

//   if (loading) return null;

//   // already logged in? bounce them out
//   if (user) {
//     const roles = user.roles || [];
//     if (roles.includes("super-admin")) {
//       return <Navigate to="/admin/vehicles" replace />;
//     }
//     if (roles.includes("car-admin")) {
//       return <Navigate to="/admin/vehicles" replace />;
//     }
//     if (roles.includes("image-admin")) {
//       return <Navigate to="/admin/images" replace />;
//     }
//     if (roles.includes("enquiry-admin")) {
//       return <Navigate to="/admin/requests" replace />;
//     }
//     // fallback for any other user
//     return <Navigate to="/used-cars" replace />;
//   }

//   const onSubmit = async ({ email, password }) => {
//     setServerError("");
//     try {
//       const { data: loginData } = await api.post("/auth/login", {
//         email,
//         password,
//       });
//       localStorage.setItem("accessToken", loginData.accessToken);
//       const { data: meData } = await api.get("/auth/me", {
//         headers: { Authorization: `Bearer ${loginData.accessToken}` },
//       });
//       setAuth({ user: meData.user, token: loginData.accessToken });

//       const roles = meData.user.roles || [];
//       if (roles.includes("super-admin")) {
//         navigate("/admin/vehicles");
//       } else if (roles.includes("car-admin")) {
//         navigate("/admin/vehicles");
//       } else if (roles.includes("image-admin")) {
//         navigate("/admin/images");
//       } else if (roles.includes("enquiry-admin")) {
//         navigate("/admin/requests");
//       } else {
//         navigate("/used-cars");
//       }
//     } catch (err) {
//       setServerError(err.response?.data?.message || "Login failed");
//     }
//   };

//   return (
//     <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen font-sans">
//       {/* ─── Left: Branding ─── */}
//       <div className="hidden md:flex items-center justify-center bg-white from-primary to-secondary pt-14">
//         <div
//           onClick={() => navigate("/")}
//           className="text-center text-darksec cursor-pointer"
//         >
//           {/* logo */}
//           <Link to="/" className="flex items-center justify-center">
//             <img
//               src={logo}
//               alt="ZORQK Logo"
//               className="h-16 w-auto object-contain"
//             />
//           </Link>
//           <p className="text-primary text-lg opacity-75">
//             Join ZORQK and accelerate your car-trading journey.
//           </p>
//           <img
//             src={carImage}
//             alt="Sleek car"
//             className="w-full h-full object-cover"
//           />
//         </div>
//       </div>

//       {/* ─── Right: Form ─── */}
//       <div className="flex items-center justify-center p-8 bg-primary">
//         <form
//           onSubmit={handleSubmit(onSubmit)}
//           className="w-full max-w-md px-2 space-y-6"
//         >
//           <div
//             onClick={() => navigate("/")}
//             className="md:hidden text-center text-darksec cursor-pointer"
//           >
//             {/* logo */}
//             <Link to="/" className="flex items-center justify-center">
//               <img
//                 src={logo2}
//                 alt="ZORQK Logo"
//                 className="h-10 w-auto object-contain"
//               />
//             </Link>
//           </div>
//           <h2 className="text-2xl font-semibold text-white text-center">
//             Sign in to your account
//           </h2>

//           {serverError && (
//             <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded">
//               {serverError}
//             </div>
//           )}

//           {/* Email */}
//           <div className="space-y-1">
//             <label
//               htmlFor="email"
//               className="block text-sm font-medium text-white"
//             >
//               Email
//             </label>
//             <div className="relative">
//               <AtSymbolIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
//               <input
//                 id="email"
//                 type="email"
//                 {...register("email")}
//                 placeholder="you@example.com"
//                 className="w-full pl-10 pr-4 py-2 border border-secondary-accent dark:border-secondary rounded-lg
//                           placeholder-secondary-accent dark:placeholder-secondary
//                            focus:outline-none focus:ring-2 focus:ring-primary transition"
//               />
//             </div>
//             {errors.email && (
//               <p className="text-sm text-danger">{errors.email.message}</p>
//             )}
//           </div>

//           {/* Password */}
//           <div className="space-y-1">
//             <label
//               htmlFor="password"
//               className="block text-sm font-medium text-white"
//             >
//               Password
//             </label>
//             <div className="relative">
//               <LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
//               <input
//                 id="password"
//                 type={showPwd ? "text" : "password"}
//                 {...register("password")}
//                 placeholder="••••••••"
//                 className="w-full pl-10 pr-10 py-2 border border-secondary-accent dark:border-secondary rounded-lg
//                             placeholder-secondary-accent dark:placeholder-secondary
//                            focus:outline-none focus:ring-2 focus:ring-primary transition"
//               />
//               <button
//                 type="button"
//                 onClick={() => setShowPwd((v) => !v)}
//                 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-secondary dark:text-secondary-accent"
//                 aria-label={showPwd ? "Hide password" : "Show password"}
//               >
//                 {showPwd ? <EyeSlashIcon /> : <EyeIcon />}
//               </button>
//             </div>
//             {errors.password && (
//               <p className="text-sm text-danger">{errors.password.message}</p>
//             )}
//           </div>

//           {/* Forgot & Remember */}
//           <div className="flex items-center justify-between text-sm">
//             <label className="inline-flex items-center space-x-2 text-secondary dark:text-secondary-accent">
//               <input
//                 type="checkbox"
//                 className="h-4 w-4 text-primary border-secondary-accent dark:border-secondary rounded"
//               />
//               <span>Remember me</span>
//             </label>
//             <Link to="/forgot-password" className="text-accent hover:underline">
//               Forgot password?
//             </Link>
//           </div>

//           {/* Submit */}
//           <button
//             type="submit"
//             disabled={isSubmitting}
//             className={`w-full py-3 font-semibold rounded-lg
//               bg-accent text-white shadow-md
//               hover:bg-primary/90 transition
//               ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
//           >
//             {isSubmitting ? "Signing in…" : "Login"}
//           </button>

//           {/* Sign up link */}
//           <p className="text-center text-sm text-secondary dark:text-secondary-accent">
//             Don’t have an account?{" "}
//             <Link to="/signup" className="text-accent hover:underline">
//               Sign up
//             </Link>
//           </p>

//           {/* Social Icons */}
//           <div className="flex items-center justify-center space-x-4 pt-4">
//             {["facebook-f", "instagram", "youtube"].map((icon) => (
//               <Link
//                 key={icon}
//                 to="#"
//                 className="text-secondary dark:text-secondary-accent hover:text-primary transition"
//               >
//                 <i className={`fab fa-${icon}`} />
//               </Link>
//             ))}
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// }
